import React, { useState, useEffect } from 'react';
import {
  Card,
  CardTitle,
  FormGroup,
  Label,
  Input,
  InputGroup,
  Button,
  Row,
  Col,
} from 'reactstrap';
import Required from 'components/Required';
import UploaderMultiple from 'components/UploaderMultiple';
import HorariosAtencionComponent from 'components/Consultorios/HorariosAtencion';
import { getMatriculateInfo } from '../../features/matriculates/matriculatesAPI';
import {
  getAllConsultoriosCokiba,
} from '../../features/consultorios/consultoriosAPI';
import {
  ConsultorioCokiba,
} from 'types/Consultorio';
import { horariosRegistradosFromConsultorios } from '../../utils/horariosValidacion';
import {
  AltaStaffMultiple as AltaStaffMultipleType,
  StaffMultipleRequest,
} from 'types/Staff';

interface MatriculateInfo {
  matricula: number;
  nombre: string;
}

type SetData = React.Dispatch<React.SetStateAction<StaffMultipleRequest>>;

interface Props {
  isLoading: boolean;
  setLoading: (isLoading: boolean) => void;
  staff: AltaStaffMultipleType;
  data: StaffMultipleRequest;
  setData: SetData;
  formValidation: Record<string, boolean>;
  eliminarAltaStaff:(index: number) => void;
  canUpdateByState?: boolean;
  canUpdateAttachs?: boolean;
}

export default function AltaStaffMultiple({
  isLoading,
  setLoading,
  staff,
  data,
  setData,
  formValidation,
  eliminarAltaStaff,
  canUpdateByState = true,
  canUpdateAttachs = true,
}: Props) {
  const [search, setSearch] = useState('');

  const [matriculateStaff, setMatriculateStaff] = useState<MatriculateInfo>({
    matricula: 0,
    nombre: '',
  });

  useEffect(() => {
    if (!!data.id && !!staff.matriculate_id) {
      const matricula = `${staff.matriculate_id}`;
      setSearch(matricula);
      onSearch(matricula);
    }
  }, [data.id]);

  const onSearch = async (query: string) => {
    try {
      if (query) {
        setLoading(true);

        setMatriculateStaff({
          matricula: 0,
          nombre: '',
        });
        setData((prev: StaffMultipleRequest) => ({
          ...prev,
          staff_alta: prev.staff_alta.map(item =>
            item.id === staff.id
              ? { ...item, matriculate_id: null }
              : item,
          ),
        }));

        const matriculate: MatriculateInfo = await getMatriculateInfo(+query) as MatriculateInfo;
        setMatriculateStaff(matriculate);

        const consultorios: ConsultorioCokiba[] = await getAllConsultoriosCokiba(query) as ConsultorioCokiba[];
        if (consultorios) {
          const ordenDias = [
            'LUNES',
            'MARTES',
            'MIERCOLES',
            'JUEVES',
            'VIERNES',
            'SABADO',
            'DOMINGO',
          ];

          const horarios = horariosRegistradosFromConsultorios(consultorios)
            .sort((a, b) => {
              const diffDias = ordenDias.indexOf(a.diaSemana) - ordenDias.indexOf(b.diaSemana);
              if (diffDias !== 0) return diffDias;
              return a.horaInicio.localeCompare(b.horaInicio);
            });

          setData((prev: StaffMultipleRequest) => ({
            ...prev,
            staff_alta: prev.staff_alta.map(item =>
              item.id === staff.id
                ? {
                    ...item,
                    matriculate_id: matriculate.matricula,
                    horarios_atencion_act: horarios,
                  }
                : item,
            ),
          }));
        }
      }
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      id={`${staff.id}`}
      style={{ padding: '20px 40px' }}
    >
      <Row>
        <Col sm={10}>
          <CardTitle>Nuevo Staff</CardTitle>
        </Col>
        <Col sm={2} className='text-right'>
          <Button
            color='danger'
            type='button'
            onClick={() => eliminarAltaStaff(staff.id)}
            size='sm'
            disabled={isLoading || !canUpdateByState}
          >
            ✕
          </Button>
        </Col>
      </Row>
      <FormGroup>
        <Row className="mt-2 mb-2">
          <Col md="6">
            <Label for="search">
              Buscar Matriculado Staff <Required />
            </Label>
            <InputGroup>
              <Input
                id="search"
                name="search"
                type="text"
                placeholder="Buscar por Matrícula..."
                invalid={formValidation[`matricula_${staff.id}`] === false}
                onChange={ event => setSearch(event.target.value) }
                onKeyUp={ event => event.key === 'Enter' && onSearch(search) }
                value={ search }
                disabled={isLoading || !canUpdateByState}
              />
              <button
                type="button"
                className="btn-primary"
                style={{
                  border: `1px solid '#dddddd'`,
                  borderBottomRightRadius: '.25rem',
                  borderTopRightRadius: '.25rem',
                  width: '40px',
                }}
                onClick={ () => onSearch(search) }
                disabled={isLoading || !canUpdateByState}
              >
                <i className="fas fa-search"></i>
              </button>
            </InputGroup>
            { !!matriculateStaff.matricula
              ?
                <div className="border rounded p-2 bg-light">
                  <div className="ml-2 mb-2">
                    <strong>Matrícula:</strong> {matriculateStaff.matricula}
                  </div>
                  <div className="ml-2 mb-2">
                    <strong>Nombre:</strong> {matriculateStaff.nombre}
                  </div>
                </div>
              : null
            }
            <Input
              id={`es_director_${staff.id}`}
              name={`es_director_${staff.id}`}
              type="checkbox"
              checked={!!staff.es_director}
              invalid={formValidation[`es_director_${staff.id}`] === false}
              onChange={e =>
                setData({
                  ...data,
                  staff_alta: data.staff_alta.map(item => {
                    return item.id === staff.id
                      ? { ...item, es_director: e.target.checked }
                      : item;
                  }),
                })
              }
              disabled={isLoading || !canUpdateByState}
            />
            <Label
              for={`es_director_${staff.id}`}
              style={{
                marginBottom: 0,
                marginLeft: '5px',
                verticalAlign: 'bottom',
              }}
            >
              Es Director
            </Label>
          </Col>
          <Col md="6">
            <Label for="documentacion">
              Días y horarios de atención <Required />
            </Label>
            <HorariosAtencionComponent
              value={staff.horarios_atencion}
              actuales={staff.horarios_atencion_act}
              onChange={horarios =>
                setData({
                  ...data,
                  staff_alta: data.staff_alta.map(item =>
                    item.id === staff.id
                      ? { ...item, horarios_atencion: horarios }
                      : item,
                  ),
                })
              }
              disabled={isLoading || !canUpdateByState}
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row className="mt-4 mb-2">
          <Col md="6">
            <Label for={`titulos_grado_${staff.matriculate_id}`}>
              Título de grado del profesional de la kinesiología <Required />
            </Label><br/>
            <UploaderMultiple
              name={`titulos_grado_${staff.matriculate_id}`}
              required
              multiple
              attachs={staff.attachs}
              canUpdateAttachs={canUpdateAttachs}
            />
          </Col>
          <Col md="6">
            <Label for={`constancia_cuit_${staff.matriculate_id}`}>
              Constancia de CUIT del profesional de la kinesiología <Required />
            </Label><br/>
            <UploaderMultiple
              name={`constancia_cuit_${staff.matriculate_id}`}
              required
              accept={['application/pdf']}
              multiple
              attachs={staff.attachs}
              canUpdateAttachs={canUpdateAttachs}
            />
          </Col>
        </Row>
      </FormGroup>
    </Card>
  );
}
